import { createAppKit } from '@reown/appkit/react';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { baseSepolia, mainnet, type AppKitNetwork } from '@reown/appkit/networks';
import { cookieStorage, createStorage, http } from 'wagmi';
import { QueryClient } from '@tanstack/react-query';
import { chainKey, environment, type ChainKey } from './environment';

// Get project ID from environment
const projectId = environment.projectId;

// Define metadata for your app
const metadata = {
  name: 'Sahara Token Vesting',
  description: 'Sahara token vesting and distribution platform',
  url: 'https://saharaai.com',
  icons: ['https://saharaai.com/icon.png']
};

// The AppKit network object for each chain this dApp can be built for. Keyed
// by the same ChainKey as the environment map, so the two cannot drift: adding
// a chain there is a type error here until it is added here too.
const APPKIT_NETWORKS: Record<ChainKey, AppKitNetwork> = {
  baseSepolia,
  mainnet
};

// Exactly one network is offered, the one this bundle was built for. Listing
// only the selected chain is what makes allowUnsupportedChain: false useful --
// AppKit treats everything not in this array as unsupported and prompts the
// user to switch, so a wallet sitting on mainnet cannot silently read a
// testnet factory address (or the reverse, which is the expensive direction).
//
// AppKit types this as a non-empty tuple, [AppKitNetwork, ...AppKitNetwork[]],
// so the annotation has to be a tuple type rather than AppKitNetwork[]. It can
// no longer be `typeof baseSepolia`: the element is now chosen at build time
// and the two chain objects are different types.
const selectedNetwork = APPKIT_NETWORKS[chainKey];
const networks: [AppKitNetwork, ...AppKitNetwork[]] = [selectedNetwork];

// `environment.rpcUrl` was dead configuration until this existed.
//
// Every read this dApp makes goes through wagmi's public client, and with no
// `transports` entry the adapter builds that client from Reown's own shared
// endpoint — so VITE_RPC_URL reached nothing, the documented escape hatch in
// environment.ts did not work, and mainnet traffic for the entire roster went
// to a rate-limited endpoint shared with every other AppKit project.
//
// Keyed by chain id because the adapter looks the entry up as
// `transports[network.id]`. AppKit wraps whatever is passed here in a
// `fallback([ours, reown])` for chains its relay supports, so the configured
// endpoint becomes PRIMARY and Reown's becomes the backstop rather than the
// default. `networks` holds exactly one chain, so exactly one entry is needed.
const transports = {
  [Number(selectedNetwork.id)]: http(environment.rpcUrl)
};

// Create Wagmi Adapter
export const wagmiAdapter = new WagmiAdapter({
  storage: createStorage({
    storage: cookieStorage
  }),
  ssr: false,
  projectId,
  networks,
  transports
});

// Create AppKit instance
export const modal = createAppKit({
  adapters: [wagmiAdapter],
  projectId,
  networks,
  metadata,
  features: {
    analytics: true,
    email: false,
    socials: []
  },
  themeMode: 'dark',
  themeVariables: {
    '--w3m-accent': '#f8ffad',
    '--w3m-color-mix': '#1a1a1a',
    '--w3m-color-mix-strength': 25,
    '--w3m-border-radius-master': '12px',
    '--w3m-font-family': 'Inter, ui-sans-serif, system-ui, sans-serif'
  },
  defaultNetwork: networks[0],
  // allowUnsupportedChain: false will force network switching
  allowUnsupportedChain: false
});

// Create QueryClient for React Query
export const queryClient = new QueryClient();

// Export Wagmi config
export const wagmiConfig = wagmiAdapter.wagmiConfig;
